import { getApiErrorField } from '../../api/apiError'

/**
 * Aksiyon statü değişimi hatasını kullanıcıya **açıklayan** i18n anahtarına eşler. Backend VALIDATION_ERROR
 * gövdesindeki `field`'a bakar; bilinen bir kural ihlaliyse ona özel metin, değilse genel statü hatası.
 *
 * <p>Özel durumlar: {@code subActionsNotComplete} — ana görev, açık alt görevleri varken 331'e
 * çekilemez (backend {@code ActionCommandService.closeParentTerminally}); {@code parentDeadlinePassed};
 * {@code completionNoteRequired} — onaya gönderme notu boş; {@code completionNote} — sınırı aşıyor. Genel "Statü güncellenemedi" mesajı bunların
 * hiçbirinde nedeni söylemiyordu.
 */
export function statusChangeErrorKey(err: unknown): string {
  switch (getApiErrorField(err)) {
    case 'subActionsNotComplete':
      return 'actions.detail.status.subActionsNotComplete'
    /*
     * 22.09.2026 — gecikmiş alt görev açık statüye çekilirken yeni termin ZORUNLUdur ve üst görevinkini
     * aşamaz; üst görevin termini geçmişse seçilebilir gün kalmaz. Sunucu bu çıkmazı açık bir alanla
     * bildirir, ekran da çözüm yolunu yazar: önce ANA görevin hedef teslim tarihi güncellenmeli.
     */
    case 'parentDeadlinePassed':
      return 'actions.detail.overdueExit.parentDeadlinePassed'
    /*
     * 29.09.2026 — onaya gönderme notu ZORUNLU oldu (105/FR-025b tersine çevrildi). Pop-up zaten boş
     * göndermeyi engelliyor; bu dal sunucunun son sözünü kullanıcıya AÇIKLAR (eski bir sekmeden ya
     * da doğrudan API'den gelen istekte genel "Statü güncellenemedi" nedeni söylemiyordu).
     */
    case 'completionNoteRequired':
      return 'actions.detail.completionNote.required'
    /*
     * Aynı alanın UZUNLUK ihlali ayrı bir koddur (`completionNote`, Bean Validation'dan gelir):
     * ikisi tek mesaja bağlansaydı sınırı aşan uzun bir not "zorunludur" diye bildirilirdi.
     */
    case 'completionNote':
      return 'actions.detail.completionNote.tooLong'
    default:
      return 'actions.detail.status.error'
  }
}

/**
 * Alt aksiyon **oluşturma** hatasını kullanıcıya **açıklayan** i18n anahtarına eşler (aynı kalıp:
 * backend `field` → özel metin, bilinmiyorsa genel hata).
 *
 * <p>Özel durumlar:
 *
 * <ul>
 *   <li>{@code parentOverdue} — ana aksiyon gecikmiş; önce yeni termin verilip Gecikti'den çıkılmalı
 *       (analist kuralı 2026-08-11; backend {@code ActionDetailService.requireParentNotOverdue}).
 *       Ekrandaki buton bu durumda zaten pasiftir, ama saat dilimi/yarış durumlarında (kayıt açıkken
 *       gece yarısını geçmek, farklı dilimdeki istemci) istek yine de gidebilir — kullanıcı o zaman
 *       nedeni görmelidir.
 *   <li>{@code deadlineAfterParent} — alt aksiyonun termini ana aksiyonunkinden ileri olamaz.
 *   <li>{@code tooManySubActionAssignees} — tek istekte seçilebilecek sorumlu sayısı aşıldı (20.10.2026;
 *       her sorumlu bir alt görev açar).
 * </ul>
 */
export function subActionCreateErrorKey(err: unknown): string {
  switch (getApiErrorField(err)) {
    case 'parentOverdue':
      return 'actions.detail.subActions.parentOverdue'
    case 'deadlineAfterParent':
      return 'actions.detail.subActions.deadlineAfterParent'
    case 'actionNotPending':
      return 'actions.detail.subActions.onlyWhenPending'
    // 20.10.2026 — tek istekte seçilebilecek sorumlu sayısı aşıldı (her sorumlu bir alt görev).
    // Sayı metinde geçmez: tek tanımı backend'de (MeetingLimits.MAX_SUBACTION_ASSIGNEES_PER_CREATE).
    case 'tooManySubActionAssignees':
      return 'actions.detail.subActions.tooManyAssignees'
    default:
      return 'actions.detail.subActions.error'
  }
}

/**
 * Alt görev **silme** hatasını açıklayan i18n anahtarına eşler (2026-09-17).
 *
 * <p>{@code atLeastOneAssignee} — **otomatik** bir alt görev silinirken sunucu, onu doğuran Süreç
 * Destek Yöneticisi atamasını üst görevden de kaldırmak zorundadır (aksi hâlde sorumlu senkronu alt
 * görevi yeniden üretir); o kaldırma üst görevi sorumlusuz bırakacaksa işlem reddedilir
 * ({@code ActionDetailService.removeParentSecondaryAssignees}). Nadir bir uç durum DEĞİLDİR: canlı dev
 * verisinde otomatik alt görevlerin yaklaşık üçte birinde üst görevin tek sorumlusu odur. Jenerik
 * "Alt görev silinemedi." mesajı kullanıcıya ne yapacağını söylemiyordu.
 *
 * <p>Hem liste ekranı (Görevler → Sil) hem Görev Detay'daki alt görev tablosu bunu kullanır — aynı
 * başarısızlığı iki ekranda farklı anlatmamak için.
 */
export function subActionDeleteErrorKey(err: unknown): string {
  if (getApiErrorField(err) === 'atLeastOneAssignee') {
    return 'actions.detail.subActions.deleteLeavesParentUnassigned'
  }
  return 'actions.detail.subActions.deleteError'
}

/**
 * Alt aksiyon **güncelleme** (Alt Aksiyon Detay) hatasını açıklayan i18n anahtarına eşler.
 *
 * <p>{@code deadlineAfterParent} backend'de <b>hem oluşturma hem güncelleme</b> yolunda fırlatılır
 * ({@code ActionDetailService.requireDeadlineNotAfterParent}); iki ekranın aynı kuralı farklı
 * anlatması (biri gerekçeli, diğeri jenerik) tutarsızlıktı. Metin oluşturma yoluyla <b>ortaktır</b>.
 */
export function subActionSaveErrorKey(err: unknown): string {
  if (getApiErrorField(err) === 'deadlineAfterParent') {
    return 'actions.detail.subActions.deadlineAfterParent'
  }
  return 'actions.detail.subActionPage.saveError'
}

/**
 * **Sorumlu ekleme** hatasını açıklayan i18n anahtarına eşler.
 *
 * <p>Özel durumlar:
 *
 * <ul>
 *   <li>{@code tooManyAssignees} — AK-4 sorumlu üst sınırı aşıldı. <b>Sayı i18n metninde
 *       geçmez</b>: sınırın tek tanımı backend'de ({@code MeetingLimits.MAX_ASSIGNEES_PER_ACTION}) ve
 *       istemciye gönderilmiyor; metne "15" yazmak, sınır değiştiğinde kullanıcıya yanlış söyleyen
 *       ikinci bir kopya olurdu.
 *   <li>{@code overdueAssignee} — <b>geciken</b> aksiyona Yan Sorumlu eklenemez (kullanıcı kuralı
 *       2026-08-17); çözüm önce hedef teslim tarihini güncellemektir. Ekran bunu zaten uyarıyla önler,
 *       ama iki taraf arasında yarış mümkündür (aynı anda değişen termin) → uç da reddeder.
 * </ul>
 *
 * <p>Varsayılan metin mükerrer sicili ("zaten ekli olabilir") anlatır — backend o durumda alan olarak
 * {@code employeeId} döner ve en sık görülen ekleme hatası odur.
 */
export function assigneeAddErrorKey(err: unknown): string {
  switch (getApiErrorField(err)) {
    case 'tooManyAssignees':
      return 'actions.detail.assignees.tooMany'
    case 'overdueAssignee':
      return 'actions.detail.assignees.overdueBlocked'
    // Ana Sorumlu, Yan Sorumlu olarak da eklenemez (2026-08-12) — "zaten ekli" değil, rol çakışmasıdır:
    // çözümü farklıdır (önce Ana Sorumlu değiştirilir), o yüzden ayrı metin.
    case 'responsibleRoleConflict':
      return 'actions.detail.roleConflict.secondaryIsMain'
    // 085/FR-008 — aksiyon "Aksiyon Bekleniyor" değilken yapı değiştirilemez. Jenerik "sorumlu
    // eklenemedi" mesajı nedeni söylemiyordu: kullanıcı kişiyi/yetkiyi sorun sanıyordu.
    case 'actionNotPending':
      return 'actions.detail.assignees.onlyWhenPending'
    default:
      return 'actions.detail.assignees.addError'
  }
}

/**
 * **Ana Sorumlu atama** hatasını açıklayan i18n anahtarına eşler. {@code responsibleRoleConflict} —
 * seçilen kişi zaten Yan Sorumlu; sistem rolü sessizce taşımaz (kullanıcı kuralı 2026-08-12), önce Yan
 * Sorumlu listesinden çıkarılmalıdır. Ekran bunu önden de engeller; bayat ekranda/iki sekmede uçtan gelir.
 */
export function mainResponsibleSetErrorKey(err: unknown): string {
  switch (getApiErrorField(err)) {
    case 'responsibleRoleConflict':
      return 'actions.detail.roleConflict.mainIsSecondary'
    case 'tooManyAssignees':
      return 'actions.detail.assignees.tooMany'
    default:
      return 'actions.detail.mainResponsible.setError'
  }
}

/**
 * **Sorumlu çıkarma** hatasını açıklayan i18n anahtarına eşler. Ekran "en az bir sorumlu" kuralını
 * önden de kontrol eder, ama <b>her</b> çıkarma hatasını o mesajla anlatmak yanlıştı: kilitli/silinmiş
 * kayıt ya da yetki dışı bir hata da "en az bir sorumlu bulunmalıdır" diye görünüyordu.
 */
export function assigneeRemoveErrorKey(err: unknown): string {
  switch (getApiErrorField(err)) {
    case 'atLeastOneAssignee':
      return 'actions.detail.assignees.minOne'
    // Ana Sorumlu zorunludur → çıkarılamaz (2026-08-12). Ekran bunu önden de kontrol eder, ama bayat
    // ekranda/iki sekmede backend'den gelebilir; jenerik mesaj yerine gerekçesi gösterilir.
    case 'mainResponsible':
      return 'actions.detail.mainResponsible.cannotRemove'
    case 'actionNotPending':
      return 'actions.detail.assignees.onlyWhenPendingRemove'
    default:
      return 'actions.detail.assignees.removeError'
  }
}

/**
 * **Not düzenleme/silme** hatasını i18n anahtarına eşler.
 *
 * <p>{@code actionTerminal} — görev SONLANMIŞ (Kapatıldı / Arşivlendi) statüdeyken içerik
 * değiştirilemez (2026-08-26). Ekran bu düğmeleri zaten gizler; bu yol **bayat ekran** ve iki-sekme
 * durumları içindir. Jenerik "not silinemedi" mesajı nedeni söylemediği için ayrı metin verilir —
 * projedeki diğer statü kilitleriyle (`actionNotPending`) aynı gerekçe.
 */
export function noteMutationErrorKey(err: unknown, fallback: string): string {
  return getApiErrorField(err) === 'actionTerminal'
    ? 'actions.detail.notes.terminalBlocked'
    : fallback
}

/** Dosya eki ekleme/kaldırma hatası — gerekçe için bkz. {@link noteMutationErrorKey}. */
export function documentMutationErrorKey(err: unknown, fallback: string): string {
  return getApiErrorField(err) === 'actionTerminal'
    ? 'actions.detail.documents.terminalBlocked'
    : fallback
}
